import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, FormControl, FormControlLabel, IconButton, Switch, TextField, Tooltip } from "@mui/material";
import { Add, Clear } from "@mui/icons-material";
import useUbicaciones from "../../../hooks/hookUbicacion";
import { MODO_EDITAR } from "../../../domain/evento/schema";
import MapaEvento from "../MapaEvento";
import SeccionEvento from "./SeccionEvento";

const BotonOpcion = ({ activo, onClick, titulo, descripcion }) => (
  <button
    type="button"
    onClick={onClick}
    className={`mr-2 mb-2 ${activo ? "boton-tipo-active" : "boton-tipo"}`}
  >
    <div className="d-flex flex-column">
      <span className="text-left">{titulo}</span>
      <span className="descripcion-boton text-left">{descripcion}</span>
    </div>
    <span className="indicator-wrapper">
      <span className="indicator-fuera"></span>
      <span className="indicator-dentro"></span>
    </span>
  </button>
);

const CamposFecha = ({ fecha, indice, onFechaChange }) => (
  <>
    <FormControl>
      <small>Fecha</small>
      <TextField
        type="date"
        size="small"
        value={fecha.fecha}
        onChange={(e) => onFechaChange(indice, "fecha", e.target.value)}
      />
    </FormControl>
    <FormControl>
      <small>Hora de inicio</small>
      <TextField
        type="time"
        size="small"
        value={fecha.hora_inicio}
        onChange={(e) => onFechaChange(indice, "hora_inicio", e.target.value)}
      />
    </FormControl>
    <FormControl>
      <small>Hora de finalización</small>
      <TextField
        type="time"
        size="small"
        value={fecha.hora_fin}
        onChange={(e) => onFechaChange(indice, "hora_fin", e.target.value)}
      />
    </FormControl>
  </>
);

const CampoAcceso = ({ fecha, indice, onFechaChange }) => (
  <FormControl>
    <small>Inicio de acceso al evento</small>
    <small style={{ color: "#666", fontSize: "11px" }}>
      Fecha y hora desde la cual se pueden escanear tickets
    </small>
    <TextField
      type="datetime-local"
      size="small"
      value={fecha.acceso_inicio}
      onChange={(e) => onFechaChange(indice, "acceso_inicio", e.target.value)}
    />
  </FormControl>
);

const DateSection = ({
  form,
  onChange,
  onFechaChange,
  onAgregarFecha,
  onEliminarFecha,
  errores,
  abierta,
  onToggle,
  modo,
}) => {
  const { fechas, direccion, ciudad } = form;
  const [variosDias, setVariosDias] = useState(fechas.length > 1);

  const {
    provincias,
    ciudades,
    provSeleccionada,
    setProvSeleccionada,
    provNombre,
    cargandoProvincias,
    resolverProvinciaDeCiudad,
  } = useUbicaciones();

  /* En edición el evento trae la ciudad guardada pero no su provincia, así que
   * se la deduce una sola vez para poder poblar el select de ciudades. */
  const provinciaResuelta = useRef(false);
  useEffect(() => {
    if (provinciaResuelta.current || modo !== MODO_EDITAR || !ciudad || provSeleccionada) {
      return;
    }
    provinciaResuelta.current = true;
    resolverProvinciaDeCiudad(ciudad);
  }, [modo, ciudad, provSeleccionada, resolverProvinciaDeCiudad]);

  useEffect(() => {
    setVariosDias(fechas.length > 1);
  }, [fechas.length]);

  const cambiarVariosDias = (valor) => {
    setVariosDias(valor);
    if (!valor && fechas.length > 1) {
      onChange("fechas", [{ ...fechas[0], oculta: false }]);
    }
  };

  const cambiarProvincia = (e) => {
    setProvSeleccionada(e.target.value);
    onChange("ciudad", "");
  };

  return (
    <SeccionEvento
      id="date"
      titulo="Fecha y ubicación"
      tituloExpandido="¿Cuándo y dónde es tu evento?"
      descripcion="Indicá cuándo se realiza tu evento y en qué lugar."
      requerido
      tieneError={Boolean(errores.fecha || errores.direccion)}
      abierta={abierta}
      onToggle={onToggle}
    >
      <div className="d-flex flex-wrap flex-row gap-3">
        <BotonOpcion
          activo={!variosDias}
          onClick={() => cambiarVariosDias(false)}
          titulo="Una sola fecha"
          descripcion="Evento que dura solo un día"
        />
        <BotonOpcion
          activo={variosDias}
          onClick={() => cambiarVariosDias(true)}
          titulo="Varias fechas"
          descripcion="Eventos de varios días"
        />
      </div>

      <div className="date-hora">
        {!variosDias ? (
          <div className="data-hora-form">
            <div className="d-flex gap-4 mt-3 mb-3 flex-wrap">
              <CamposFecha fecha={fechas[0]} indice={0} onFechaChange={onFechaChange} />
            </div>
            <div className="d-flex gap-4 mt-1">
              <CampoAcceso fecha={fechas[0]} indice={0} onFechaChange={onFechaChange} />
            </div>
          </div>
        ) : (
          <>
            {fechas.map((fecha, indice) => (
              <div key={fecha.id ?? `nueva-${indice}`} className="d-flex gap-4 mt-3 flex-wrap align-items-end">
                <CamposFecha fecha={fecha} indice={indice} onFechaChange={onFechaChange} />
                <CampoAcceso fecha={fecha} indice={indice} onFechaChange={onFechaChange} />
                <Tooltip
                  arrow
                  title="Una fecha oculta no se muestra en la página del evento y sus tarifas no se venden online, incluidos los abonos que la incluyan."
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={!fecha.oculta}
                        onChange={(e) => onFechaChange(indice, "oculta", !e.target.checked)}
                      />
                    }
                    label={fecha.oculta ? "Oculta" : "Visible"}
                  />
                </Tooltip>
                {fechas.length > 1 && (
                  <IconButton
                    aria-label={`Eliminar fecha ${indice + 1}`}
                    onClick={() => onEliminarFecha(indice)}
                  >
                    <Clear />
                  </IconButton>
                )}
              </div>
            ))}
            <Button
              variant="text"
              startIcon={<Add />}
              onClick={onAgregarFecha}
              sx={{ marginTop: "10px" }}
            >
              Agregar fecha
            </Button>
          </>
        )}

        {errores.fecha && (
          <Alert severity="error" sx={{ my: 2 }}>
            {errores.fecha}
          </Alert>
        )}
      </div>

      <FormControl fullWidth>
        <p className="subtitulo-seccion-evento-expanded mt-2">
          Ubicación <span className="text-danger">*</span>
        </p>
        <p className="descrip-seccion-evento-expanded">
          Agregá la dirección o el lugar donde se llevará a cabo tu evento
        </p>

        <div className="d-flex flex-wrap gap-1 align-items-center">
          <FormControl>
            <select
              value={provSeleccionada}
              onChange={cambiarProvincia}
              className="input-select-evento"
            >
              <option value="">
                {cargandoProvincias
                  ? "Cargando provincias..."
                  : provincias.length === 0
                    ? "No se pudieron cargar las provincias"
                    : "Seleccione una provincia"}
              </option>
              {provincias.map((provincia) => (
                <option key={provincia.id} value={provincia.id}>
                  {provincia.name}
                </option>
              ))}
            </select>
          </FormControl>

          <FormControl>
            <select
              value={ciudad}
              onChange={(e) => onChange("ciudad", e.target.value)}
              className="input-select-evento"
              disabled={!provSeleccionada}
            >
              <option value="">Seleccione una ciudad</option>
              {/* la ciudad guardada se muestra aunque todavía no llegó su lista */}
              {ciudad && !ciudades.some((item) => item.name === ciudad) && (
                <option value={ciudad}>{ciudad}</option>
              )}
              {ciudades.map((item) => (
                <option key={item.id} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
          </FormControl>

          <FormControl style={{ width: "60%" }}>
            <TextField
              sx={{ marginTop: "5px", marginBottom: "15px", input: { padding: "10px 15px" } }}
              name="direccion"
              placeholder="Dirección"
              value={direccion}
              onChange={(e) => onChange("direccion", e.target.value)}
            />
          </FormControl>
        </div>

        {errores.direccion && <Alert severity="error">{errores.direccion}</Alert>}

        <div className="d-flex justify-content-center mt-3">
          <MapaEvento direccion={direccion} ciudad={ciudad} provincia={provNombre} />
        </div>
      </FormControl>

    </SeccionEvento>
  );
};

export default DateSection;
